import { Button } from '@heidi/design-system/button';
import { Routines } from '@heidi/design-system/icons';
import type { LucideIcon } from 'lucide-react';
import { useIntl } from 'react-intl';

import { InlineApprovalCardShell } from '../../inline-approval/inline-approval-card-shell';

export type RoutineSuggestionMode = 'create' | 'update';

interface RoutineSuggestionCardProps {
  mode?: RoutineSuggestionMode;
  /** One-line change from the follow-up model; replaces the update subtitle. */
  change?: string;
  onCreate: () => void;
  onDismiss: () => void;
}

/**
 * Elevated capture prompt shown when a reply contains repeatable work.
 * Create = save a new routine; update = fold durable edits into the
 * routine this chat already belongs to. Actions stay explicit: nothing
 * starts until the user chooses the CTA.
 */
export const RoutineSuggestionCard = ({
  mode = 'create',
  change,
  onCreate,
  onDismiss,
}: RoutineSuggestionCardProps) => {
  const intl = useIntl();
  const isUpdate = mode === 'update';

  const title = isUpdate
    ? intl.formatMessage({
        defaultMessage: 'Update your routine',
        id: 'vOBrkyon5+',
        description: 'Title for a suggestion to fold chat edits into an existing routine',
      })
    : intl.formatMessage({
        defaultMessage: 'Save as a routine',
        id: 'AIJT7EmuPX',
        description: 'Title for a suggestion to save repeatable chat work as a routine',
      });

  const subtitle = isUpdate
    ? change ||
      intl.formatMessage({
        defaultMessage:
          'Remember the durable changes from this run so future runs of this routine keep them.',
        id: 'jiBpk53XBw',
        description: 'Benefit of updating an existing routine from the current chat',
      })
    : intl.formatMessage({
        defaultMessage:
          'Save the repeatable steps and run them again on demand or on a schedule.',
        id: 'BRLxTGnnEc',
        description: 'Benefit of saving the current work as a routine',
      });

  const createLabel = isUpdate
    ? intl.formatMessage({
        defaultMessage: 'Update routine',
        id: 'sd+tVEZK67',
        description: 'Button to fold chat edits into the existing routine',
      })
    : intl.formatMessage({
        defaultMessage: 'Create routine',
        id: 'DOsIvRsQYn',
        description: 'Button to begin creating a routine from the current chat',
      });

  const dismissLabel = intl.formatMessage({
    defaultMessage: 'Not now',
    id: 'sioOg7Nl9i',
    description: 'Button to dismiss a routine suggestion',
  });

  return (
    <section
      aria-label={title}
      className="mb-2"
      data-testid="routine-suggestion-card"
      data-mode={mode}
    >
      <InlineApprovalCardShell
        Icon={Routines as LucideIcon}
        title={title}
        subtitle={subtitle}
      >
        <div className="flex items-center justify-end gap-2">
          <Button type="button" size="sm" variant="ghost" onClick={onDismiss}>
            {dismissLabel}
          </Button>
          <Button type="button" size="sm" onClick={onCreate}>
            {createLabel}
          </Button>
        </div>
      </InlineApprovalCardShell>
    </section>
  );
};
